import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { GungNotificationService, gungAddRemoveSpinner } from 'gung-common';
import { first } from 'rxjs';
import {
  PunchoutCustomerConfig,
  PunchoutOrderAuthMode,
  PunchoutOrderIntakeHandlerOption,
  PunchoutProtocol
} from '../../../models/punchout';
import { BackendInterceptor } from '../../../services/backend-interceptor/backend-interceptor.service';
import { GungModalService } from '../../../services/gung-modal/gung-modal.service';
import { PunchoutProtocolHandler } from '../../../services/punchout/handlers/punchout-protocol.handler';
import { PunchoutConfigAdminService } from '../../../services/punchout/punchout-config-admin.service';
import { PUNCHOUT_PROTOCOL_HANDLERS } from '../../../services/punchout/punchout.tokens';
import { User } from '../../../state/auth/types';
import { getPunchoutUserLatestLogin, punchoutExampleLoginUrl } from '../../../utils/punchout-user-utils';

interface PunchoutMapperOption {
  identifier: string;
  label: string;
}

@Component({
  selector: 'lib-gung-settings-punchout-edit-modal',
  templateUrl: './gung-settings-punchout-edit-modal.component.html',
  styleUrl: './gung-settings-punchout-edit-modal.component.scss',
  standalone: false
})
export class GungSettingsPunchoutEditModalComponent implements OnInit {
  protected fb = inject(FormBuilder);
  protected adminService = inject(PunchoutConfigAdminService);
  protected backendInterceptor = inject(BackendInterceptor);
  protected modalService = inject(GungModalService);
  protected notificationService = inject(GungNotificationService);
  protected translateService = inject(TranslateService);
  protected handlers = inject<PunchoutProtocolHandler[]>(PUNCHOUT_PROTOCOL_HANDLERS);
  public activeModal = inject(NgbActiveModal);

  initial?: PunchoutCustomerConfig;
  delegate?: GungModalService;
  componentId = GungSettingsPunchoutEditModalComponent.name;

  /**
   * Users wired to this config, already filtered and sorted by the configs tab. Undefined means the
   * user list could not be read, and the whole section is then omitted rather than shown empty.
   */
  connectedUsers?: User[];

  form!: FormGroup;
  saving = false;
  deleting = false;
  mapperOptions: PunchoutMapperOption[] = [];
  intakeHandlerOptions: PunchoutOrderIntakeHandlerOption[] = [];
  connectedUsersExpanded = false;

  ngOnInit(): void {
    const initialFormat: PunchoutProtocol = this.initial?.format ?? 'oci';
    this.mapperOptions = this.optionsFor(initialFormat);
    const initialQualifier = this.resolveInitialQualifier(initialFormat);

    this.form = this.fb.group({
      name: this.fb.control(this.initial?.name ?? '', [Validators.required]),
      format: this.fb.control(initialFormat, [Validators.required]),
      qualifier: this.fb.control(initialQualifier, [Validators.required]),
      redirectUrl: this.fb.control(this.initial?.redirectUrl ?? '/punchout/login', [Validators.required]),
      orderIntakeEnabled: this.fb.control(this.initial?.orderIntakeEnabled ?? false),
      orderIntakeHandlerId: this.fb.control(this.initial?.orderIntakeHandlerId ?? ''),
      orderGlnMappingEnabled: this.fb.control(this.initial?.orderGlnMappingEnabled ?? true),
      orderAuthMode: this.fb.control<PunchoutOrderAuthMode>(this.initial?.orderAuthMode ?? 'NONE'),
      orderSharedSecret: this.fb.control(this.initial?.orderSharedSecret ?? ''),
      orderBasicAuthUsername: this.fb.control(this.initial?.orderBasicAuthUsername ?? ''),
      orderBasicAuthPassword: this.fb.control(this.initial?.orderBasicAuthPassword ?? '')
    });

    this.form.controls['format'].valueChanges.subscribe((format: PunchoutProtocol) => {
      this.mapperOptions = this.optionsFor(format);
      const fallback = this.mapperOptions[0]?.identifier ?? '';
      this.form.controls['qualifier'].setValue(fallback);
    });

    this.adminService
      .orderIntakeHandlers()
      .pipe(first())
      .subscribe(options => {
        this.intakeHandlerOptions = options;
        const current = this.form.controls['orderIntakeHandlerId'].value;
        if (!current && options.length > 0) {
          this.form.controls['orderIntakeHandlerId'].setValue(options[0].id);
        }
      });
  }

  /**
   * True only for a config that exists in the backend. A copy arrives pre-filled from another
   * config but without an id, so it is a new config despite `initial` being set.
   */
  get isExisting(): boolean {
    return !!this.initial?.id;
  }

  get orderAuthMode(): PunchoutOrderAuthMode {
    return this.form?.controls['orderAuthMode'].value ?? 'NONE';
  }

  get format(): PunchoutProtocol {
    return this.form?.controls['format'].value ?? 'oci';
  }

  /**
   * Where the buyer's procurement system posts the punchout login. This is a backend endpoint
   * (PunchoutOciPublicController / PunchoutCxmlPublicController), so it is built on the backend
   * base and not on the frontend origin. The backend answers it by redirecting the buyer to
   * the redirect URL below.
   */
  get loginUrl(): string {
    return this.backendUrl(`/public/punchout/${this.format}/login`);
  }

  // The intake URL uses the config id. The URL exists only after the config is saved.
  get intakeUrl(): string | undefined {
    if (!this.initial?.id) {
      return undefined;
    }
    return this.backendUrl(`/public/punchout/cxml/order/${this.initial.id}`);
  }

  /** The frontend distribution the backend should redirect the buyer to, on the domain in use. */
  get suggestedRedirectUrl(): string {
    return `${window.location.origin}/punchout/login`;
  }

  /**
   * Only shown for a saved config: a new config and a copy are both id-less, so nothing can be
   * connected to them yet. Same rule intakeUrl uses.
   */
  get showConnectedUsers(): boolean {
    return this.isExisting && !!this.connectedUsers;
  }

  /** Non-optional view of the list, so the template does not have to narrow the input. */
  get connectedUserList(): User[] {
    return this.connectedUsers ?? [];
  }

  /**
   * cXML login is a POSTed document (raw cXML, or a cXML-urlencoded form field), not a URL with
   * credentials in it, so there is no per-user example URL to give. The buyer gets the endpoint plus
   * the user's credentials instead.
   */
  get supportsExampleLoginUrl(): boolean {
    return this.format === 'oci';
  }

  exampleLoginUrl(user: User): string {
    return punchoutExampleLoginUrl(this.loginUrl, user.username);
  }

  latestLogin(user: User): string | undefined {
    return getPunchoutUserLatestLogin(user);
  }

  toggleConnectedUsers(): void {
    this.connectedUsersExpanded = !this.connectedUsersExpanded;
  }

  useSuggestedRedirectUrl(): void {
    const control = this.form.controls['redirectUrl'];
    control.setValue(this.suggestedRedirectUrl);
    control.markAsDirty();
  }

  copyUrl(url?: string): void {
    if (!url) {
      return;
    }
    navigator.clipboard.writeText(url).then(() => {
      this.notificationService.notify('', this.translateService.instant('COPIED_TO_CLIPBOARD'), 'success');
    });
  }

  private backendUrl(path: string): string {
    const base = (this.backendInterceptor.getBaseUrl() || window.location.origin).replace(/\/+$/, '');
    return `${base}${path}`;
  }

  save({ target }: { target: EventTarget | null }): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    gungAddRemoveSpinner(target as HTMLElement);
    this.saving = true;
    const payload: PunchoutCustomerConfig = {
      ...(this.initial ?? ({} as PunchoutCustomerConfig)),
      ...this.form.value
    };
    this.adminService
      .save(payload)
      .pipe(first())
      .subscribe({
        next: saved => {
          this.notificationService.notify(
            '',
            this.translateService.instant(this.isExisting ? 'PUNCHOUT_CONFIG_UPDATED' : 'PUNCHOUT_CONFIG_CREATED'),
            'success'
          );
          this.saving = false;
          gungAddRemoveSpinner(target as HTMLElement);
          this.close(saved);
        },
        error: () => {
          this.notificationService.notify('', this.translateService.instant('ERROR'), 'error');
          this.saving = false;
          gungAddRemoveSpinner(target as HTMLElement);
        }
      });
  }

  delete({ target }: { target: EventTarget | null }): void {
    if (!this.initial?.id) {
      return;
    }
    const initialId = this.initial.id;
    this.modalService
      .openConfirmYesNoModal('CONFIRM_DELETE', 'CONFIRM_DELETE_PUNCHOUT_CONFIG', { size: 'sm' })
      .then(ok => {
        if (!ok) {
          return;
        }
        gungAddRemoveSpinner(target as HTMLElement);
        this.deleting = true;
        this.adminService
          .delete(initialId)
          .pipe(first())
          .subscribe({
            next: () => {
              this.notificationService.notify('', this.translateService.instant('PUNCHOUT_CONFIG_DELETED'), 'success');
              this.deleting = false;
              gungAddRemoveSpinner(target as HTMLElement);
              this.close({ ...this.initial!, id: initialId });
            },
            error: () => {
              this.notificationService.notify('', this.translateService.instant('ERROR'), 'error');
              this.deleting = false;
              gungAddRemoveSpinner(target as HTMLElement);
            }
          });
      });
  }

  cancel(): void {
    this.activeModal.close(undefined);
  }

  private close(result: PunchoutCustomerConfig | undefined): void {
    const ref = this.delegate?.modalRef.get(this.componentId);
    if (ref) {
      ref.close(result);
    } else {
      this.activeModal.close(result);
    }
  }

  private optionsFor(format: PunchoutProtocol): PunchoutMapperOption[] {
    return this.handlers.filter(h => h.protocol === format).map(h => ({ identifier: h.identifier, label: h.label }));
  }

  // Keep the stored qualifier if it still matches a registered handler.
  // Otherwise, use the first available mapper for the format.
  // This keeps the dropdown from showing an unknown value.
  private resolveInitialQualifier(format: PunchoutProtocol): string {
    const stored = this.initial?.qualifier;
    if (stored && this.mapperOptions.some(o => o.identifier === stored)) {
      return stored;
    }
    return this.mapperOptions[0]?.identifier ?? '';
  }
}
